import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '@mdi/react';
import { makeStyles } from '@material-ui/core/styles';
import SvgIcon from '@material-ui/core/SvgIcon';
import StudyExplorer from '../components/StudyExplorer';
import CornerstoneViewport from '../components/CornerstoneViewport';
import downloadService from '../services/downloadService';
import apiClient from '../services/apiClient';
import { initCornerstone } from '../services/cornerstoneInit';
import { loadSeriesImageStack } from '../services/dicomLoader';
import { formatDicomDate, formatDicomTime } from '../utils/dateTimeFormatter';
import { formatPatientName } from '../utils/patientNameFormatter';
import db from '../database/db';

import { 
  Box, 
  CircularProgress, 
  IconButton, 
  Tooltip,
  Divider,
  Button,
  Typography,
  LinearProgress,
} from '@material-ui/core';

import {
  mdiCursorDefault,
  mdiHandBackRight,
  mdiMagnifyPlusOutline,
  mdiBrightness6,
  mdiRuler,
  mdiAngleAcute,
  mdiFormatTitle,
  mdiUndo,
  mdiRedo,
  mdiImageRefresh,
  mdiEraser,
} from '@mdi/js';

// Custom Reference Lines icon
const RefLinesIcon = "M9 7H15V1H17V7H23V9H17V15H23V17H17V23H15V17H9V23H7V17H1V15H7V9H1V7H7V1H9V7ZM9 15H15V9H9V15Z";

// Custom COR icon component
const CorIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <rect
      x="1"
      y="1"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      rx="2"
    />
    <text
      x="12"
      y="9.5"
      textAnchor="middle"
      fill="currentColor"
      fontSize="8.5"
      fontWeight="bold"
      fontFamily="Roboto, sans-serif"
    >
      COR
    </text>
    <line
      x1="4"
      x2="20"
      y1="12"
      y2="12"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <text
      x="12"
      y="20.5"
      textAnchor="middle"
      fill="currentColor"
      fontSize="8.5"
      fontWeight="bold"
      fontFamily="Roboto, sans-serif"
    >
      LOC
    </text>
  </SvgIcon>
);

// Custom Cobb Angle icon
const CobbAngleIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    {/* Top endplate line */}
    <path 
      d="M3 9 L21 4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    {/* Bottom endplate line */}
    <path
      d="M3 15 L21 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    {/* Top perpendicular line */}
    <path
      d="M14 6 L16 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Bottom perpendicular line */}
    <path
      d="M14 18 L16 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </SvgIcon>
);

const useStyles = makeStyles((theme) => ({
  // Layout styles
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: theme.palette.background.default,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  activeButton: {
    color: theme.palette.primary.main,
  },
  activeToolButton: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  content: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minWidth: 0,
  },
  sidebar: {
    width: 230,
    flex: '0 0 230px',
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
  },
  viewportArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1),
    gap: theme.spacing(1),
    minWidth: 0,
  },
  verticalDivider: {
    alignSelf: 'stretch',
    // margin: theme.spacing(0, 1),
    backgroundColor: theme.palette.divider,
  },
}));

function ViewerApp() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { studyId } = useParams(); // Get studyInstanceUID from URL

  const [isInitialized, setIsInitialized] = useState(false);
  const [studyLoaded, setStudyLoaded] = useState(false);
  const [viewportKey] = useState(0);
  const [referenceLinesEnabled, setReferenceLinesEnabled] = useState(false);
  const [activeViewport, setActiveViewport] = useState(null);
  const [currentStudyUID, setCurrentStudyUID] = useState(null);
  const [patientInfo, setPatientInfo] = useState({
    patientName: '',
    dateOfBirth: '',
  });
  const [viewportData, setViewportData] = useState({
    sagittal: { imageIds: [], seriesDescription: '', currentImageIndex: 0, seriesDate: '', seriesTime: '', seriesInstanceUID: null },
    axial: { imageIds: [], seriesDescription: '', currentImageIndex: 0, seriesDate: '', seriesTime: '', seriesInstanceUID: null },
    coronal: { imageIds: [], seriesDescription: '', currentImageIndex: 0, seriesDate: '', seriesTime: '', seriesInstanceUID: null },
  });
  const [coronalVisible, setCoronalVisible] = useState(false);
  const [activeTool, setActiveTool] = useState('no-tool');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadError, setDownloadError] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasAnnotations, setHasAnnotations] = useState(false);

  // Store references to viewport components
  const viewportRefs = useRef({
    sagittal: null,
    axial: null,
    coronal: null,
  });

  // Initialize Cornerstone on mount
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Initialize Cornerstone
      initCornerstone();

      if (!cancelled) {
        setIsInitialized(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Tool selection handler
  const handleToolSelect = useCallback((toolName) => {
    // Ignore if actively creating a new annotation
    if (activeViewport) {
      const vp = viewportRefs.current?.[activeViewport];
      if (vp?.isCreating?.()) return;
    }

    console.log('Tool selected:', toolName);
    
    setActiveTool(toolName);
  }, [activeViewport]);

  // Update button states based on active viewport
  const updateButtonStates = useCallback(() => {
    if (!activeViewport) {
      setCanUndo(false);
      setCanRedo(false);
      setHasAnnotations(false);
      return;
    }

    const vp = viewportRefs.current?.[activeViewport];
    if (!vp) {
      setCanUndo(false);
      setCanRedo(false);
      setHasAnnotations(false);
      return;
    }

    setCanUndo(vp.canUndo?.() || false);
    setCanRedo(vp.canRedo?.() || false);
    setHasAnnotations(vp.hasAnnotations?.() || false);
  }, [activeViewport]);

  // Keyboard shortcuts for tool selection
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Ignore if user is typing in an input field
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      // Map keys to tools
      const keyMap = {
        'escape': 'no-tool',    // ESC to deactivate
        'z': 'zoom',            // Z for zoom
        'p': 'pan',             // P for pan
        'w': 'ww/wl',           // W for window width/level
        'd': 'distance',        // D for distance
        'a': 'angle',           // A for angle
        'c': 'cobb-angle',      // C for cobb angle
        't': 'text',            // T for text
      };

      const toolName = keyMap[event.key.toLowerCase()];
      if (toolName) {
        event.preventDefault();
        handleToolSelect(toolName);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [activeTool, handleToolSelect]);

  // Listen for annotation changes to update button states
  useEffect(() => {
    if (!activeViewport) return;

    const element = viewportRefs.current[activeViewport]?.getElement?.();
    if (!element) return;

    const handleAnnotationChange = () => {
      // Defer update slightly to ensure tool state is fully updated
      setTimeout(() => {
        updateButtonStates();
      }, 0);
    };

    // Listen to various tool events
    element.addEventListener('cornerstonetoolsmeasurementadded', handleAnnotationChange);
    element.addEventListener('cornerstonetoolsmeasurementmodified', handleAnnotationChange);
    element.addEventListener('cornerstonetoolsmeasurementcompleted', handleAnnotationChange);
    element.addEventListener('cornerstonetoolsmeasurementremoved', handleAnnotationChange);

    return () => {
      if (element) {
        element.removeEventListener('cornerstonetoolsmeasurementadded', handleAnnotationChange);
        element.removeEventListener('cornerstonetoolsmeasurementmodified', handleAnnotationChange);
        element.removeEventListener('cornerstonetoolsmeasurementcompleted', handleAnnotationChange);
        element.removeEventListener('cornerstonetoolsmeasurementremoved', handleAnnotationChange);
      }
    };
  }, [activeViewport, updateButtonStates]);

  // Load study data when component mounts or studyId changes
  useEffect(() => {
    if (isInitialized && studyId) {
      loadStudy(studyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, studyId]);

  const loadStudy = async (backendStudyId) => {
    try {    
      // Step 1: Get study metadata from backend to find DICOM StudyInstanceUID
      setIsDownloading(true);
      setDownloadError(null);
      setDownloadProgress(0);

      let studyInstanceUID = null;

      try {
        const response = await apiClient.get(`/studies/${backendStudyId}`);
        studyInstanceUID = response.data.study_instance_uid;
      } catch (error) {
        console.error('Failed to get study metadata from backend:', error);
        
        if (error.response?.status === 404) {
          setDownloadError('Study not found on server');
        } else if (error.response?.status === 403) {
          setDownloadError('You do not have permission to view this study');
        } else {
          setDownloadError('Failed to load study metadata');
        }
        
        setIsDownloading(false);
        return;
      }

      // Step 2: Check if study is already cached in IndexedDB
      const isCached = await downloadService.isStudyCached(studyInstanceUID);
      
      if (!isCached) {      
        // Download study files from backend
        const downloadResult = await downloadService.downloadStudy(
          backendStudyId,
          (progress) => setDownloadProgress(progress)
        );

        if (!downloadResult.success) {
          setDownloadError(downloadResult.error);
          setIsDownloading(false);
          return;
        }
      }

      // Step 3: Load study from IndexedDB cache
      const study = await db.studies.get(studyInstanceUID);
      
      if (!study) {
        console.error('Study not found in IndexedDB after download:', studyInstanceUID);
        setDownloadError('Study download succeeded but failed to load from cache');
        setIsDownloading(false);
        return;
      }

      // Load patient info from patients table
      const patient = await db.patients.get(study.patientID);

      if (!patient) {
        console.error('Patient not found for study:', studyInstanceUID);
        setDownloadError('Patient data not found');
        setIsDownloading(false);
        return;
      }

      // Set current study UID for StudyExplorer
      setCurrentStudyUID(studyInstanceUID);

      // Load patient info for header display
      setPatientInfo({
        patientName: formatPatientName(patient.patientName) || 'Unknown Patient',
        dateOfBirth: formatDicomDate(patient.patientBirthDate),
      });

      setStudyLoaded(true);
      setIsDownloading(false);      
    } catch (error) {
      console.error('Error loading study:', error);
      setDownloadError('An unexpected error occurred while loading the study');
      setIsDownloading(false);
    }
  };

  const handleSeriesSelect = async (series) => {
    // Ignore if actively creating a new annotation
    if (activeViewport) {
      const vp = viewportRefs.current?.[activeViewport];
      if (vp?.isCreating?.()) {
        return false; // Return false to indicate action was blocked
      }
    }

    try {
      console.log('Loading series:', series);
      
      const imageIds = await loadSeriesImageStack(series.seriesInstanceUID);
      const seriesOrientation = series.orientation.toLowerCase();
      
      // Determine target viewport based on series orientation
      // LOCALIZER and CORONAL go to the right (coronal) viewport
      let targetViewport;
      if (seriesOrientation === 'localizer' || seriesOrientation === 'coronal') {
        targetViewport = 'coronal';
        setCoronalVisible(true); // Always show coronal viewport for these series
        console.log(`Routing ${seriesOrientation} series to coronal (right) viewport`);
      } else if (seriesOrientation === 'sagittal' || seriesOrientation === 'axial') {
        targetViewport = seriesOrientation;
        console.log(`Routing ${seriesOrientation} series to ${seriesOrientation} viewport`);
      } else {
        // Unknown orientation - default to sagittal
        targetViewport = 'sagittal';
        console.warn(`Unknown orientation "${seriesOrientation}", defaulting to sagittal viewport`);
      }

      console.log(`Loaded ${imageIds.length} images for ${seriesOrientation} orientation`);

      setViewportData((prev) => ({
        ...prev,
        [targetViewport]: {
          imageIds: imageIds,
          seriesDescription: series.seriesDescription,
          currentImageIndex: 0,
          seriesDate: formatDicomDate(series.seriesDate),
          seriesTime: formatDicomTime(series.seriesTime),
          seriesInstanceUID: series.seriesInstanceUID,
          seriesOrientation: seriesOrientation,
        },
      }));
    } catch (error) {
      console.error('Error loading series:', error);
      alert('Error loading series. Check console for details.');
      return false; // Return false on error
    }

    return true; // Return true to on success
  };

  // Handle viewport image index changes for reference lines
  const handleViewportImageChange = (orientation, imageIndex) => {
    setViewportData((prev) => ({
      ...prev,
      [orientation]: {
        ...prev[orientation],
        currentImageIndex: imageIndex,
      },
    }));

    // Only trigger reference lines update if this is the active viewport
    if (referenceLinesEnabled && activeViewport === orientation) {
      updateReferenceLines(orientation);
    }
  };

  // Update reference lines on all other viewports
  const updateReferenceLines = (sourceOrientation) => {
    const orientations = ['sagittal', 'axial', 'coronal'];
    orientations.forEach(orientation => {
      if (orientation !== sourceOrientation && viewportRefs.current[orientation]) {
        viewportRefs.current[orientation].updateReferenceLinesFromOther(sourceOrientation);
      }
    });
  };

  // Toggle reference lines
  const toggleReferenceLines = () => {
    // Ignore if actively creating a new annotation
    if (activeViewport) {
      const vp = viewportRefs.current?.[activeViewport];
      if (vp?.isCreating?.()) return;
    }
    
    setReferenceLinesEnabled(prev => !prev);
  };

  // Toggle coronal viewport visibility
  const toggleCoronalViewport = () => {
    // Ignore if actively creating a new annotation
    if (activeViewport) {
      const vp = viewportRefs.current?.[activeViewport];
      if (vp?.isCreating?.()) return;
    }
    
    setCoronalVisible((prevVisible) => {
      const nextVisible = !prevVisible;

      // If COR is being hidden and it was active, clear active viewport
      if (!nextVisible) {
        setActiveViewport((prevActive) => (prevActive === 'coronal' ? null : prevActive));
      }

      return nextVisible;
    });
  };

  // Only resize viewports when layout actually changes (COR show/hide, or container resize)
  useEffect(() => {
    requestAnimationFrame(() => {
      viewportRefs.current.sagittal?.resizeViewport?.();
      viewportRefs.current.axial?.resizeViewport?.();
      viewportRefs.current.coronal?.resizeViewport?.();
    });
  }, [coronalVisible]);

  // Update reference lines when active viewport changes (without resizing)
  useEffect(() => {
    if (referenceLinesEnabled && activeViewport) {
      updateReferenceLines(activeViewport);
    }
  }, [referenceLinesEnabled, activeViewport]);

  // Update undo/redo/clear button states when active viewport changes
  useEffect(() => {
    updateButtonStates();
  }, [activeViewport, updateButtonStates]);

  // Handle viewport click to set active viewport
  const handleViewportClick = (orientation) => {
    setActiveViewport(orientation);
    
    // Update reference lines immediately when viewport becomes active
    if (referenceLinesEnabled) {
      updateReferenceLines(orientation);
    }
  };

  const handleResetView = () => {
    if (!activeViewport) return;
    const vp = viewportRefs.current?.[activeViewport];
    
    // Ignore if actively creating a new annotation
    if (vp?.isCreating?.()) return;
    
    vp?.resetView?.();
  };

  const handleClearAnnotations = () => {
    if (!activeViewport) return;
    const vp = viewportRefs.current?.[activeViewport];
    
    // Ignore if actively creating a new annotation
    if (vp?.isCreating?.()) return;
    
    vp?.clearAnnotations?.();
    updateButtonStates();
  };

  const handleUndo = () => {
    if (!activeViewport) return;
    const vp = viewportRefs.current?.[activeViewport];
    
    // Ignore if actively creating a new annotation
    if (vp?.isCreating?.()) return;
    
    vp?.undo?.();
    updateButtonStates();
  };

  const handleRedo = () => {
    if (!activeViewport) return;
    const vp = viewportRefs.current?.[activeViewport];
    
    // Ignore if actively creating a new annotation
    if (vp?.isCreating?.()) return;
    
    vp?.redo?.();
    updateButtonStates();
  };

  if (!isInitialized) {
    return (
      <Box className={classes.root}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Download Progress Overlay */}
      {isDownloading && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} style={{ color: '#1976d2' }} />
          <Typography variant="h6" style={{ marginTop: 24, color: '#fff', fontWeight: 500 }}>
            {downloadProgress === 0 ? 'Preparing Download...' : 'Downloading Study...'}
          </Typography>
          {downloadProgress > 0 && (
            <Typography variant="h4" style={{ marginTop: 16, color: '#fff', fontWeight: 600 }}>
              {downloadProgress}%
            </Typography>
          )}
          {downloadProgress > 0 && (
            <Box style={{ width: '60%', maxWidth: 400, marginTop: 16 }}>
              <LinearProgress 
                variant="determinate" 
                value={downloadProgress} 
                style={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
          <Typography variant="body2" style={{ marginTop: 12, color: 'rgba(255,255,255,0.7)' }}>
            Please wait while DICOM files are downloaded
          </Typography>
        </Box>
      )}

      {/* Error Overlay */}
      {downloadError && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Typography variant="h5" style={{ color: '#f44336', marginBottom: 24, fontWeight: 600 }}>
            Error Loading Study
          </Typography>
          <Typography variant="body1" style={{ color: '#fff', marginBottom: 40, textAlign: 'center', maxWidth: 500 }}>
            {downloadError}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/library')}
          >
            Back to Library
          </Button>
        </Box>
      )}

      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          {/* Add header left controls here if needed */}
        </Box>

        <Box className={classes.headerRight}>
          {/* Tool Buttons */}
          <Tooltip title="No Tool (ESC)">
            <IconButton
              className={activeTool === 'no-tool' ? classes.activeToolButton : classes.iconButton}
              onClick={() => handleToolSelect('no-tool')}
            >
              <Icon path={mdiCursorDefault} size={1} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Pan (P)">
            <IconButton
              className={activeTool === 'pan' ? classes.activeToolButton : classes.iconButton}
              onClick={() => handleToolSelect('pan')}
            >
              <Icon path={mdiHandBackRight} size={1} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Zoom (Z)">
            <IconButton
              className={activeTool === 'zoom' ? classes.activeToolButton : classes.iconButton}
              onClick={() => handleToolSelect('zoom')}
            >
              <Icon path={mdiMagnifyPlusOutline} size={1} />
            </IconButton>
          </Tooltip>

          <Tooltip title="WW/WL (W)">
            <IconButton
              className={activeTool === 'ww/wl' ? classes.activeToolButton : classes.iconButton}
              onClick={() => handleToolSelect('ww/wl')}
            >
              <Icon path={mdiBrightness6} size={1} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Distance (D)">
            <IconButton
              className={activeTool === 'distance' ? classes.activeToolButton : classes.iconButton}
              onClick={() => handleToolSelect('distance')}
            >
              <Icon path={mdiRuler} size={1} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Angle (A)">
            <IconButton
              className={activeTool === 'angle' ? classes.activeToolButton : classes.iconButton}
              onClick={() => handleToolSelect('angle')}
            >
              <Icon path={mdiAngleAcute} size={1} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Cobb Angle (C)">
            <IconButton
              className={activeTool === 'cobb-angle' ? classes.activeToolButton : classes.iconButton}
              onClick={() => handleToolSelect('cobb-angle')}
            >
              <CobbAngleIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Text (T)">
            <IconButton
              className={activeTool === 'text' ? classes.activeToolButton : classes.iconButton}
              onClick={() => handleToolSelect('text')}
            >
              <Icon path={mdiFormatTitle} size={1} />
            </IconButton>
          </Tooltip>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem className={classes.verticalDivider} />

          <Tooltip title="Undo Annotation">
            <span>
              <IconButton 
                className={classes.iconButton}
                onClick={handleUndo}
                disabled={!canUndo}
              >
                <Icon path={mdiUndo} size={1} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Redo Annotation">
            <span>
              <IconButton 
                className={classes.iconButton}
                onClick={handleRedo}
                disabled={!canRedo}
              >
                <Icon path={mdiRedo} size={1} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Clear All Annotations">
            <span>
              <IconButton 
                className={classes.iconButton}
                onClick={handleClearAnnotations}
                disabled={!hasAnnotations}
              >
                <Icon path={mdiEraser} size={1} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Reset Zoom, Pan, WW/WL">
            <span>
              <IconButton 
                className={classes.iconButton}
                onClick={handleResetView}
                disabled={!activeViewport}
              >
                <Icon path={mdiImageRefresh} size={1} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem className={classes.verticalDivider} />

          {/* Other Control Buttons */}
          <Tooltip title="Toggle Reference Lines">
            <IconButton
              className={referenceLinesEnabled ? classes.activeButton : classes.iconButton}
              onClick={toggleReferenceLines}
            >
              <Icon path={RefLinesIcon} size={1} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Toggle Coronal View">
            <IconButton
              className={coronalVisible ? classes.activeButton : classes.iconButton}
              onClick={toggleCoronalViewport}
            >
              <CorIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Content */}
      <Box className={classes.content}>
        {/* Sidebar - Study Explorer */}
        <Box className={classes.sidebar}>
          {studyLoaded && currentStudyUID ? (
            <StudyExplorer 
              studyInstanceUID={currentStudyUID}
              onSeriesSelect={handleSeriesSelect} 
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>

        {/* Viewport Area */}
        <Box className={classes.viewportArea}>
          <CornerstoneViewport
            key={`sagittal-${viewportKey}`}
            ref={el => viewportRefs.current.sagittal = el}
            imageIds={viewportData.sagittal.imageIds}
            orientation="SAGITTAL"
            seriesInstanceUID={viewportData.sagittal.seriesInstanceUID}
            seriesDescription={viewportData.sagittal.seriesDescription}
            currentImageIndex={viewportData.sagittal.currentImageIndex}
            activeTool={activeTool}
            referenceLinesEnabled={referenceLinesEnabled}
            viewportData={viewportData}
            onImageIndexChange={(index) => handleViewportImageChange('sagittal', index)}
            isActive={activeViewport === 'sagittal'}
            onViewportClick={() => handleViewportClick('sagittal')}
            activeViewport={activeViewport}
            patientName={patientInfo.patientName}
            dateOfBirth={patientInfo.dateOfBirth}
            seriesDate={viewportData.sagittal.seriesDate}
            seriesTime={viewportData.sagittal.seriesTime}
          />
          <CornerstoneViewport
            key={`axial-${viewportKey}`}
            ref={el => viewportRefs.current.axial = el}
            imageIds={viewportData.axial.imageIds}
            orientation="AXIAL"
            seriesInstanceUID={viewportData.axial.seriesInstanceUID}
            seriesDescription={viewportData.axial.seriesDescription}
            currentImageIndex={viewportData.axial.currentImageIndex}
            activeTool={activeTool}
            referenceLinesEnabled={referenceLinesEnabled}
            viewportData={viewportData}
            onImageIndexChange={(index) => handleViewportImageChange('axial', index)}
            isActive={activeViewport === 'axial'}
            onViewportClick={() => handleViewportClick('axial')}
            activeViewport={activeViewport}
            patientName={patientInfo.patientName}
            dateOfBirth={patientInfo.dateOfBirth}
            seriesDate={viewportData.axial.seriesDate}
            seriesTime={viewportData.axial.seriesTime}
          />
          {/* Coronal viewport - hidden with CSS to preserve state when toggled */}
          <div style={{ display: coronalVisible ? 'flex' : 'none', flex: 1 }}>
            <CornerstoneViewport
              key={`coronal-${viewportKey}`}
              ref={el => viewportRefs.current.coronal = el}
              imageIds={viewportData.coronal.imageIds}
              orientation="CORONAL"
              seriesOrientation={viewportData.coronal.seriesOrientation} // NEW: Pass actual series orientation
              seriesInstanceUID={viewportData.coronal.seriesInstanceUID}
              seriesDescription={viewportData.coronal.seriesDescription}
              currentImageIndex={viewportData.coronal.currentImageIndex}
              activeTool={activeTool}
              referenceLinesEnabled={referenceLinesEnabled}
              viewportData={viewportData}
              onImageIndexChange={(index) => handleViewportImageChange('coronal', index)}
              isActive={activeViewport === 'coronal'}
              onViewportClick={() => handleViewportClick('coronal')}
              activeViewport={activeViewport}
              patientName={patientInfo.patientName}
              dateOfBirth={patientInfo.dateOfBirth}
              seriesDate={viewportData.coronal.seriesDate}
              seriesTime={viewportData.coronal.seriesTime}
            />
          </div>
        </Box>
      </Box>
    </Box>
  );
}

export default ViewerApp;